import AppTheme from "@toothfairy/shared-ui/AppTheme";
import { AppText } from "@toothfairy/shared-ui";
import React from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import Charting from "../../../Components/Charting";
import Constants from "../../../Constants";
import DashboardFilters from "./DashboardFilters";
import DashboardItem from "./DashboardItem";
import DashboardPlaceholder from "./DashboardPlaceholder";

const ChatAnalyticsView = ({
  chatAnalyticsData,
  chatAnalyticsAPIInProgress,
  agents,
  filterStartDate,
  filterEndDate,
  filterAgentId,
  onFilterStartDateChange,
  onFilterEndDateChange,
  onFilterAgentChange,
  onExportChats,
  rawChatsCount,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();

  const doughnutChartOptions = {
    legend: {
      labels: {
        color: currentTheme?.theme?.font_color,
        font: {
          family: "normalFont",
          size: currentTheme?.theme?.extra_small_font_size,
        },
      },
    },
  };

  const horizontalBarOptions = {
    indexAxis: "y",
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
        labels: {
          color: currentTheme?.theme?.font_color,
          font: {
            family: "normalFont",
            size: currentTheme?.theme?.extra_small_font_size,
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        position: "bottom",
        ticks: {
          color: currentTheme?.theme?.font_color,
          font: {
            family: "normalFont",
            size: currentTheme?.theme?.extra_small_font_size,
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: currentTheme?.theme?.font_color,
          font: {
            family: "normalFont",
            size: currentTheme?.theme?.extra_small_font_size,
          },
        },
      },
    },
  };

  const hasPIIData =
    chatAnalyticsData?.piiBreakdown &&
    Object.values(chatAnalyticsData.piiBreakdown).some((v) => v > 0);

  const hasToxicityData =
    chatAnalyticsData?.toxicityBreakdown &&
    Object.values(chatAnalyticsData.toxicityBreakdown).some((v) => v > 0);

  const hasEscalationData =
    chatAnalyticsData?.escalationReasons &&
    chatAnalyticsData.escalationReasons.length > 0;

  const hasCompletionData =
    chatAnalyticsData?.completionStats &&
    (chatAnalyticsData.completionStats.completed > 0 ||
      chatAnalyticsData.completionStats.incomplete > 0);

  const hasSatisfactionData =
    chatAnalyticsData?.satisfactionDistribution &&
    Object.values(chatAnalyticsData.satisfactionDistribution).some(
      (v) => v > 0
    );

  const hasQualityData =
    chatAnalyticsData?.qualityDistribution &&
    Object.values(chatAnalyticsData.qualityDistribution).some((v) => v > 0);

  return (
    <View style={{ paddingBottom: 40 }}>
      <DashboardFilters
        agents={agents}
        selectedAgentId={filterAgentId}
        onAgentChange={onFilterAgentChange}
        startDate={filterStartDate}
        endDate={filterEndDate}
        onStartDateChange={onFilterStartDateChange}
        onEndDateChange={onFilterEndDateChange}
        onExport={onExportChats}
        isExportDisabled={!rawChatsCount || rawChatsCount === 0}
      />
      <AppText
        style={{
          color: currentTheme?.theme?.softGrey,
          fontSize: currentTheme?.theme?.semi_small_font_size,
          marginTop: 10,
          marginBottom: 10,
          fontStyle: "italic",
        }}
        preventTranslation
      >
        {t("dashboard.chatAnalytics.aiGenerated")}
        {rawChatsCount > 0 && ` ${t("dashboard.chatAnalytics.showingChats", { count: rawChatsCount })}`}
      </AppText>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          marginTop: 10,
          marginBottom: 30,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <DashboardItem
          isFetchInProgress={chatAnalyticsAPIInProgress}
          title={t("dashboard.chatAnalytics.requestCompletion")}
          isHalfWidth={true}
          additionalTitle={
            chatAnalyticsData?.completionStats
              ? ` (${chatAnalyticsData.totalChatsAnalyzed} ${t("dashboard.chatAnalytics.chats")})`
              : ""
          }
        >
          {hasCompletionData ? (
            <Charting.AppDoughnut
              options={doughnutChartOptions}
              maxHeight={Constants.doughnutChartMaxHeight}
              dataset={{
                labels: [t("dashboard.chatAnalytics.completed"), t("dashboard.chatAnalytics.incomplete")],
                datasets: [
                  {
                    label: t("dashboard.chatAnalytics.chats"),
                    data: [
                      chatAnalyticsData.completionStats.completed,
                      chatAnalyticsData.completionStats.incomplete,
                    ],
                    backgroundColor: [
                      currentTheme?.theme?.green,
                      currentTheme?.theme?.red,
                    ],
                  },
                ],
              }}
            />
          ) : (
            <DashboardPlaceholder
              mainCaption={t("dashboard.chatAnalytics.noDataAvailable")}
              subCaption={t("dashboard.chatAnalytics.completionCaption")}
            />
          )}
        </DashboardItem>

        <DashboardItem
          isFetchInProgress={chatAnalyticsAPIInProgress}
          title={t("dashboard.chatAnalytics.customerSatisfaction")}
          isHalfWidth={true}
          additionalTitle={
            chatAnalyticsData?.averageSatisfaction
              ? ` ${t("dashboard.chatAnalytics.avg", { score: chatAnalyticsData.averageSatisfaction.toFixed(1) })}`
              : ""
          }
        >
          {hasSatisfactionData ? (
            <Charting.AppBarChart
              vertical={false}
              maxHeight={Constants.chartMaxHeight}
              options={horizontalBarOptions}
              dataset={{
                labels: [
                  t("dashboard.chatAnalytics.veryLow"),
                  t("dashboard.chatAnalytics.low"),
                  t("dashboard.chatAnalytics.medium"),
                  t("dashboard.chatAnalytics.high"),
                  t("dashboard.chatAnalytics.veryHigh"),
                ],
                datasets: [
                  {
                    label: t("dashboard.chatAnalytics.chats"),
                    data: [
                      chatAnalyticsData.satisfactionDistribution.veryLow,
                      chatAnalyticsData.satisfactionDistribution.low,
                      chatAnalyticsData.satisfactionDistribution.medium,
                      chatAnalyticsData.satisfactionDistribution.high,
                      chatAnalyticsData.satisfactionDistribution.veryHigh,
                    ],
                    borderColor: [
                      currentTheme?.theme?.red,
                      currentTheme?.theme?.orange,
                      currentTheme?.theme?.yellow,
                      currentTheme?.theme?.primary,
                      currentTheme?.theme?.green,
                    ],
                    borderRadius: 10,
                    backgroundColor: [
                      currentTheme?.theme?.red,
                      currentTheme?.theme?.orange,
                      currentTheme?.theme?.yellow,
                      currentTheme?.theme?.primary,
                      currentTheme?.theme?.green,
                    ],
                    maxBarThickness: 50,
                  },
                ],
              }}
            />
          ) : (
            <DashboardPlaceholder
              mainCaption={t("dashboard.chatAnalytics.noDataAvailable")}
              subCaption={t("dashboard.chatAnalytics.satisfactionCaption")}
            />
          )}
        </DashboardItem>
      </View>

      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: 30,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <DashboardItem
          isFetchInProgress={chatAnalyticsAPIInProgress}
          title={t("dashboard.chatAnalytics.piiDetection")}
          isHalfWidth={true}
          additionalTitle={
            chatAnalyticsData?.piiStats
              ? ` ${t("dashboard.chatAnalytics.detections", { count: chatAnalyticsData.piiStats.detected })}`
              : ""
          }
        >
          {hasPIIData ? (
            <Charting.AppBarChart
              vertical={false}
              maxHeight={Constants.chartMaxHeight}
              options={horizontalBarOptions}
              dataset={{
                labels: Object.keys(chatAnalyticsData.piiBreakdown),
                datasets: [
                  {
                    label: t("dashboard.chatAnalytics.occurrences"),
                    data: Object.values(chatAnalyticsData.piiBreakdown),
                    borderColor: currentTheme?.theme?.orange,
                    borderRadius: 10,
                    backgroundColor: currentTheme?.theme?.orange,
                    maxBarThickness: 50,
                  },
                ],
              }}
            />
          ) : (
            <DashboardPlaceholder
              mainCaption={t("dashboard.chatAnalytics.noPiiDetected")}
              subCaption={t("dashboard.chatAnalytics.piiCaption")}
            />
          )}
        </DashboardItem>

        <DashboardItem
          isFetchInProgress={chatAnalyticsAPIInProgress}
          title={t("dashboard.chatAnalytics.toxicityAnalysis")}
          isHalfWidth={true}
          additionalTitle={
            chatAnalyticsData?.toxicityStats
              ? ` ${t("dashboard.chatAnalytics.toxicityAvg", { percentage: (chatAnalyticsData.toxicityStats.averageScore * 100).toFixed(1) })}`
              : ""
          }
        >
          {hasToxicityData ? (
            <Charting.AppBarChart
              vertical={false}
              maxHeight={Constants.chartMaxHeight}
              options={horizontalBarOptions}
              dataset={{
                labels: Object.keys(chatAnalyticsData.toxicityBreakdown),
                datasets: [
                  {
                    label: t("dashboard.chatAnalytics.occurrences"),
                    data: Object.values(chatAnalyticsData.toxicityBreakdown),
                    borderColor: currentTheme?.theme?.red,
                    borderRadius: 10,
                    backgroundColor: currentTheme?.theme?.red,
                    maxBarThickness: 50,
                  },
                ],
              }}
            />
          ) : (
            <DashboardPlaceholder
              mainCaption={t("dashboard.chatAnalytics.noToxicityDetected")}
              subCaption={t("dashboard.chatAnalytics.toxicityCaption")}
            />
          )}
        </DashboardItem>
      </View>

      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: 30,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <DashboardItem
          isFetchInProgress={chatAnalyticsAPIInProgress}
          title={t("dashboard.chatAnalytics.escalations")}
          isHalfWidth={true}
          additionalTitle={
            chatAnalyticsData?.escalationStats
              ? ` (${t("dashboard.chatAnalytics.escalationsTotal", { count: chatAnalyticsData.escalationStats.total })})`
              : ""
          }
        >
          {hasEscalationData ? (
            <Charting.AppBarChart
              vertical={false}
              maxHeight={Constants.chartMaxHeight}
              options={horizontalBarOptions}
              dataset={{
                labels: chatAnalyticsData.escalationReasons.map((r) => {
                  const label = r.reason || t("dashboard.costingView.unknown");
                  return label.length > 20 ? label.substring(0, 20) + "..." : label;
                }),
                datasets: [
                  {
                    label: t("dashboard.chatAnalytics.count"),
                    data: chatAnalyticsData.escalationReasons.map(
                      (r) => r.count
                    ),
                    borderColor: currentTheme?.theme?.purple,
                    borderRadius: 10,
                    backgroundColor: currentTheme?.theme?.purple,
                    maxBarThickness: 50,
                  },
                ],
              }}
            />
          ) : (
            <DashboardPlaceholder
              mainCaption={t("dashboard.chatAnalytics.noEscalations")}
              subCaption={t("dashboard.chatAnalytics.escalationsCaption")}
            />
          )}
        </DashboardItem>

        <DashboardItem
          isFetchInProgress={chatAnalyticsAPIInProgress}
          title={t("dashboard.chatAnalytics.qualityScores")}
          isHalfWidth={true}
        >
          {hasQualityData ? (
            <Charting.AppDoughnut
              options={doughnutChartOptions}
              maxHeight={Constants.doughnutChartMaxHeight}
              dataset={{
                labels: [
                  t("dashboard.chatAnalytics.excellent"),
                  t("dashboard.chatAnalytics.good"),
                  t("dashboard.chatAnalytics.fair"),
                  t("dashboard.chatAnalytics.poor"),
                  t("dashboard.chatAnalytics.unrated"),
                ],
                datasets: [
                  {
                    label: t("dashboard.chatAnalytics.chats"),
                    data: [
                      chatAnalyticsData.qualityDistribution.excellent,
                      chatAnalyticsData.qualityDistribution.good,
                      chatAnalyticsData.qualityDistribution.fair,
                      chatAnalyticsData.qualityDistribution.poor,
                      chatAnalyticsData.qualityDistribution.unrated,
                    ],
                    backgroundColor: [
                      currentTheme?.theme?.green,
                      currentTheme?.theme?.primary,
                      currentTheme?.theme?.yellow,
                      currentTheme?.theme?.red,
                      currentTheme?.theme?.mediumGrey,
                    ],
                  },
                ],
              }}
            />
          ) : (
            <DashboardPlaceholder
              mainCaption={t("dashboard.chatAnalytics.noDataAvailable")}
              subCaption={t("dashboard.chatAnalytics.qualityCaption")}
            />
          )}
        </DashboardItem>
      </View>
    </View>
  );
};

export default ChatAnalyticsView;
